// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Wrench, Plus, Search } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, SetupGuide, Select, Pagination, DataTable, Skeleton, Input, CardKebabMenu, ConfirmDialog } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useMaintenanceRequests, useDeleteMaintenanceRequest } from '@/hooks/useMaintenanceRequests'
import { useListSort } from '@/hooks/useListSort'
import { useProperties } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useSelectedPropertyStore } from '@/stores'
import i18n from '@/i18n'
import type { MaintenanceRequest } from '@/types'

type StatusFilter = '' | 'open' | 'in_progress' | 'completed'

export function MaintenanceListPage() {
  const { t } = useTranslation('maintenance')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<StatusFilter>('')
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const [pendingDelete, setPendingDelete] = useState<MaintenanceRequest | null>(null)
  const deleteRequest = useDeleteMaintenanceRequest()
  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const { data, isLoading, isFetching, error } = useMaintenanceRequests(
    page,
    status || undefined,
    selectedPropertyId ?? undefined,
    undefined,
    sortParams.sort,
    sortParams.sort_order as 'asc' | 'desc' | undefined,
    from || undefined,
    to || undefined,
    searchQuery || undefined
  )
  const { data: propertiesData } = useProperties()
  const { data: accountSettings } = useAccountSettings()

  // Second query for total count (no filters) to decide setup guide vs empty state
  const { data: totalData } = useMaintenanceRequests(1)
  const totalCount = totalData?.meta?.total_count ?? 0

  const requests = data?.data || []

  const properties = propertiesData?.data || []
  const hasProperties = properties.length > 0

  const propertyOptions = [
    { value: '', label: t('filters.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const statusTabs: { value: StatusFilter; label: string }[] = [
    { value: '', label: t('status.all') },
    { value: 'open', label: t('status.open') },
    { value: 'in_progress', label: t('status.in_progress') },
    { value: 'completed', label: t('status.completed') },
  ]

  const getStatusBadge = (request: MaintenanceRequest) => {
    switch (request.status) {
      case 'completed':
        return <Badge variant="success">{t('status.completed')}</Badge>
      case 'in_progress':
        return <Badge variant="info">{t('status.in_progress')}</Badge>
      case 'open':
      default:
        return <Badge variant="warning">{t('status.open')}</Badge>
    }
  }

  const getPriorityBadge = (request: MaintenanceRequest) => {
    switch (request.priority) {
      case 'urgent':
        return <Badge variant="error">{t('priority.urgent')}</Badge>
      case 'normal':
        return <Badge variant="ghost">{t('priority.normal')}</Badge>
      case 'low':
      default:
        return <Badge variant="ghost">{t('priority.low')}</Badge>
    }
  }

  const getDuration = (createdAt: string) => {
    const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24))
    if (days === 0) return t('labels.today')
    if (days === 1) return `1 ${t('labels.day')}`
    return `${days} ${t('labels.days')}`
  }

  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const columns: Column<MaintenanceRequest>[] = [
    {
      key: 'actions',
      header: '',
      className: 'w-10',
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <CardKebabMenu
            ariaLabel={tc('labels.actions_menu')}
            items={[
              { label: tc('actions.view'), onClick: () => navigate(`/maintenances/${r.id}`) },
              { label: tc('actions.edit'), onClick: () => navigate(`/maintenances/${r.id}/edit`) },
              { label: tc('actions.delete'), variant: 'error', onClick: () => setPendingDelete(r) },
            ]}
          />
        </div>
      ),
    },
    { key: 'title', header: t('labels.title'), render: (r) => <span className="font-medium">{r.title}</span> },
    { key: 'status', header: tc('labels.status'), sortable: true, render: (r) => getStatusBadge(r) },
    { key: 'priority', header: t('labels.priority'), sortable: true, render: (r) => getPriorityBadge(r) },
    { key: 'unit_number', header: t('labels.unit'), sortable: true, render: (r) => r.unit?.name || '-' },
    { key: 'customer', header: t('labels.customer'), hideBelow: 'md', render: (r) => r.customer?.name || '-' },
    { key: 'category', header: t('labels.category'), hideBelow: 'md', render: (r) => {
      const cats = accountSettings?.settings?.maintenance_categories
      const cat = cats?.find((c) => typeof c !== 'string' && c.key === r.category)
      if (cat && typeof cat !== 'string') return i18n.language === 'th' ? cat.th : cat.en
      // No matching configured category (orphaned/custom key, or the account
      // never customized maintenance_categories) — fall through to the fixed
      // default keys via i18n.exists(), else render the raw category as-is.
      if (!r.category) return '-'
      return i18n.exists(`maintenance:categories.${r.category}`) ? t(`categories.${r.category}`) : r.category
    } },
    { key: 'property', header: t('labels.property'), hideBelow: 'lg', render: (r) => r.property?.name || '-' },
    { key: 'created_at', header: t('labels.date'), hideBelow: 'lg', sortable: true, render: (r) => formatDate(r.created_at) },
    { key: 'duration', header: t('labels.duration'), hideBelow: 'lg', render: (r) => getDuration(r.created_at) },
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns. Labels
  // reuse the column-header translations; the default updated_at gets a shared key.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'priority', label: t('labels.priority') },
    { value: 'created_at', label: tc('sort.by_newest') },
    { value: 'status', label: tc('labels.status') },
    { value: 'unit_number', label: t('labels.unit') },
  ]

  // Count active secondary filters for the mobile summary badge (search excluded)
  const activeFilterCount = [status, from, to].filter(Boolean).length

  return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          description={t('description')}
          isRefreshing={isFetching && !isLoading}
          titleAction={
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tc('labels.select_property')}
              value={selectedPropertyId ? String(selectedPropertyId) : ''}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
                setPage(1)
              }}
              options={propertyOptions}
              className="max-w-[9rem]"
              size="sm"
            />
          }
          actions={
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/maintenances/new')}>
              {t('actions.new_request')}
            </Button>
          }
        />

        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('filters.search_placeholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
            />
          </div>

          {/* Mobile-only toggle for secondary filters */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="maintenance-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="maintenance-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            {/* Status tabs — single-line horizontal scroll on mobile */}
            <div className="flex gap-1 self-center overflow-x-auto">
              {statusTabs.map((tab) => (
                <Button
                  key={tab.value}
                  size="sm"
                  variant={status === tab.value ? 'primary' : 'ghost'}
                  className="flex-shrink-0 whitespace-nowrap"
                  onClick={() => {
                    setStatus(tab.value)
                    setPage(1)
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full sm:flex-1 sm:min-w-[280px]">
              <Input
                label={t('filters.date_from')}
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1) }}
              />
              <Input
                label={t('filters.date_to')}
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1) }}
              />
            </div>
          </div>
        </Card>

        {/* Content */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={t('messages.error_loading')}
              description={t('messages.error_description')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          </Card>
        ) : !requests.length ? (
          <Card>
            {totalCount === 0 ? (
              <SetupGuide
                icon={<Wrench className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.prereq_property'),
                    met: hasProperties,
                    link: '/properties',
                    linkLabel: t('setup_guide.prereq_setup_property'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                  { title: t('setup_guide.step_3_title'), description: t('setup_guide.step_3_description') },
                ]}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate('/maintenances/new')}
                    disabled={!hasProperties}
                  >
                    {t('actions.new_request')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Wrench className="w-12 h-12" />}
                title={t('empty.title')}
                description={t('empty.description')}
                action={
                  <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/maintenances/new')}>
                    {t('actions.new_request')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={requests}
              keyExtractor={(r) => r.id}
              onRowClick={(r) => navigate(`/maintenances/${r.id}`)}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
            />
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}

        <ConfirmDialog
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            if (!pendingDelete) return
            await deleteRequest.mutateAsync(pendingDelete.id)
            setPendingDelete(null)
          }}
          title={tc('actions.delete')}
          message={t('messages.delete_confirm', { title: pendingDelete?.title })}
          confirmLabel={tc('actions.delete')}
          cancelLabel={tc('actions.cancel')}
          variant="error"
          loading={deleteRequest.isPending}
        />
      </div>
  )
}
